import type { AxiosInstance, InternalAxiosRequestConfig } from 'axios'

/**
 * WHAT THE TOP PROGRESS BAR LISTENS TO (components/ProgressBar.tsx).
 *
 * Only requests that follow something the person did: a click, a key, going back. The app
 * also polls on timers (the chat badge, the inbox count, getting-started), and a bar that
 * crept across the top every thirty seconds for those would be noise that teaches people
 * to ignore it. So a request counts if it starts within a second of the last action.
 */
let lastAction = 0
let pending = 0
const listeners = new Set<(n: number) => void>()
const mark = () => { lastAction = performance.now() }

if (typeof window !== 'undefined') {
  window.addEventListener('pointerdown', mark, true)
  window.addEventListener('keydown', mark, true)
  window.addEventListener('popstate', mark)
}

const emit = () => listeners.forEach((f) => f(pending))

/** Called for every page change as well: a link can also be followed from code. */
export const markAction = mark

export function onPending(f: (n: number) => void): () => void {
  listeners.add(f)
  return () => { listeners.delete(f) }
}

type Tracked = InternalAxiosRequestConfig & { __lqProgress?: boolean }

/** Count an axios instance's user-started requests. */
export function track(instance: AxiosInstance) {
  instance.interceptors.request.use((config: Tracked) => {
    if (performance.now() - lastAction < 1000) { config.__lqProgress = true; pending++; emit() }
    return config
  })
  const done = (config?: Tracked) => {
    if (config?.__lqProgress) { config.__lqProgress = false; pending = Math.max(0, pending - 1); emit() }
  }
  instance.interceptors.response.use(
    (r) => { done(r.config as Tracked); return r },
    (e) => { done(e?.config as Tracked); return Promise.reject(e) },
  )
}
